import { Menu, Layout, Avatar, Radio, Space } from 'antd'
import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import classNames from 'classnames'
import { NavLink, useHistory, useLocation } from 'react-router-dom'
import _ from 'lodash'
import style from './style.module.scss'
import '../appNavMenu/index.css'

const { Sider } = Layout

const AppNavCategoryHeader = ({ name }) => {
  const [openKeys, setOpenKeys] = useState([''])
  const [displayAgentName, setDisplayAgentName] = useState(name)
  const [displayAgentIcon, setDisplayAgentIcon] = useState(null)
  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation()
  const onOpenChange = keys => {
    setOpenKeys(keys.length ? [keys[keys.length - 1]] : [''])
  }
  const onSelect = keys => {
    if (_.includes(['item_0', 'item_6', 'item_7'], keys.key)) setOpenKeys([])
  }
  const apps = useSelector(state => state.apps)
  const { activeBuilderAgent, aiAgentsBuilderList } = apps

  // Load AI agents builder list when component mounts
  useEffect(() => {
    if (apps.active?.id && (!aiAgentsBuilderList || aiAgentsBuilderList.length === 0)) {
      dispatch({
        type: 'apps/AI_AGENTS_BUILDER_LIST',
        payload: {
          data: apps.active,
          appSettings: apps.appSettings,
        },
      })
    }
  }, [dispatch, apps.active, apps.appSettings, aiAgentsBuilderList])

  useEffect(() => {
    const nextName = (activeBuilderAgent && activeBuilderAgent.name) || ''
    if (nextName && nextName.trim().length) {
      setDisplayAgentName(nextName)
    }
    // Update stable icon only when we have a truthy icon (string or URL)
    const nextIcon = activeBuilderAgent && activeBuilderAgent.icon
    if (nextIcon) {
      setDisplayAgentIcon(nextIcon)
    }
  }, [activeBuilderAgent])

  // Get other agents (similar to how AppNavMenuFooter gets other apps)
  const otherAgents = _.chain(aiAgentsBuilderList || [])
    .reject({ uid: activeBuilderAgent?.uid }) // Exclude current active agent
    .take(2) // Take the first two elements
    .value()

  const onChange = e => {
    // Handle agent switching logic (always execute to prevent UI breaks)
    const selectedAgent = aiAgentsBuilderList?.find(agent => agent.uid === e.target.value)
    localStorage.setItem('ccActiveAgentId', selectedAgent.uid)

    if (selectedAgent) {
      // Update the active builder agent in Redux state
      dispatch({
        type: 'apps/SET_STATE',
        payload: {
          activeBuilderAgent: selectedAgent,
        },
      })

      // Close the popup
      setOpenKeys([])

      // Instead of reloading, dispatch any necessary data refresh actions
      // This ensures the agent's data is loaded properly
      if (apps.active && apps.appSettings) {
        // Refresh any agent-specific data that might be needed
        dispatch({
          type: 'apps/GET_AVAILABLE_MODELS',
          payload: {},
        })
      }

      // Navigate to the same page but with the selected agent UID
      try {
        const currentPath = location.pathname
        const newPath = currentPath.replace(
          /(\/app\/[^/]+\/ai-agents\/)([^/]+)(\/.*)?/,
          (match, prefix, _uid, suffix = '') => `${prefix}${selectedAgent.uid}${suffix}`,
        )
        if (newPath !== currentPath) {
          history.push(newPath)
        }
      } catch (err) {
        history.push(`/app/${apps.active?.id}/ai-agents/${selectedAgent.uid}/instructions`)
      }
    }
  }

  const handleAgentListItemHover = () => {}

  const handleAgentListItemClick = agentId => {
    // Always allow clicking to prevent UI breaks, even for the active agent
    const selectedAgent = aiAgentsBuilderList?.find(agent => agent.uid === agentId)
    if (selectedAgent) {
      // Update the active builder agent in Redux state
      dispatch({
        type: 'apps/SET_STATE',
        payload: {
          activeBuilderAgent: selectedAgent,
        },
      })

      // Close the popup
      setOpenKeys([])

      // Instead of reloading, dispatch any necessary data refresh actions
      if (apps.active && apps.appSettings) {
        dispatch({
          type: 'apps/GET_AVAILABLE_MODELS',
          payload: {},
        })
      }

      // Navigate to the same page but for the selected agent
      try {
        const currentPath = location.pathname
        const newPath = currentPath.replace(
          /(\/app\/[^/]+\/ai-agents\/)([^/]+)(\/.*)?/,
          (match, prefix, _uid, suffix = '') => `${prefix}${selectedAgent.uid}${suffix}`,
        )
        if (newPath !== currentPath) {
          history.push(newPath)
        }
      } catch (err) {
        history.push(`/app/${apps.active?.id}/ai-agents/${selectedAgent.uid}/instructions`)
      }
    }
  }

  return (
    <Sider
      width={208}
      className={classNames('site-layout-background', style.appNavCategoryHeaderWrapper)}
    >
      <Menu
        className={style.appNavCategoryHeaderCustom}
        mode="vertical"
        openKeys={openKeys}
        onOpenChange={onOpenChange}
        onSelect={onSelect}
      >
        {/* AI AGENTS */}
        <Menu.SubMenu
          key="agents"
          popupOffset={otherAgents.length ? [0, 20] : [0, 30]}
          className={classNames(
            style.appNavMenuCustomSubMenu,
            style.appNavSubMenuCustomAgentListButton,
            'appInfoMenu',
          )}
          title={
            <div
              className={classNames(style.appNavSubMenuCustomTitle)}
              style={{
                padding: '16px',
                borderRadius: '8px',
              }}
            >
              <Avatar
                src={displayAgentIcon || activeBuilderAgent?.icon || null}
                style={{
                  backgroundColor: '#F5F5F5',
                  color: '#727272',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minWidth: '32px',
                  minHeight: '32px',
                  borderRadius: '50%',
                  flexShrink: 0,
                }}
              >
                {displayAgentName
                  ? displayAgentName.match(/[A-Za-z]/)?.[0].toUpperCase()
                  : ''}
              </Avatar>
              <div>
                <div
                  style={{
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#181D27',
                    fontFamily: 'Satoshi',
                  }}
                >
                  {displayAgentName && displayAgentName.length > 14
                    ? `${displayAgentName.slice(0, 14)}...`
                    : displayAgentName || name}
                </div>
                <div />
              </div>
            </div>
          }
        >
          <Radio.Group
            className={style.radioGroupFullWidth}
            style={{
              backgroundColor: '#fff',
            }}
            onChange={onChange}
            value={activeBuilderAgent?.uid}
          >
            <Space
              direction="vertical"
              className={style.spaceFullWidth}
              style={{ backgroundColor: '#fff' }}
            >
              <Radio
                style={{
                  height: 'fit-content',
                  display: 'flex',
                  flexDirection: 'row-reverse',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '8px',
                  width: '100%',
                  paddingRight: '16px',
                  paddingLeft: '10px',
                }}
                value={activeBuilderAgent?.uid}
                className={style.appNavRadioGroup}
              >
                <Menu.Item
                  className={classNames(
                    style.appNavMenuCustomItem,
                    style.appNavMenuCustomItemChild,
                    style.appNavMenuCustomItemFooter,
                  )}
                  style={{ height: '50px', cursor: 'default', paddingInline: '0px' }}
                  onItemHover={handleAgentListItemHover}
                  onClick={() => handleAgentListItemClick(activeBuilderAgent?.uid)}
                  itemKey={activeBuilderAgent?.uid}
                  key={activeBuilderAgent?.uid}
                  disabled
                >
                  <div
                    className={classNames(
                      style.appNavMenuResourcesWrapper,
                      style.appNavMenuAgentsWrapperName,
                    )}
                  >
                    <Avatar
                      src={displayAgentIcon || activeBuilderAgent?.icon || null}
                      style={{
                        backgroundColor: '#F5F5F5',
                        color: '#727272',
                        fontSize: '14px',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: '32px',
                        height: '32px',
                        borderRadius: '8px',
                        flexShrink: 0,
                      }}
                    >
                      {displayAgentName
                        ? displayAgentName.match(/[A-Za-z]/)?.[0].toUpperCase()
                        : ''}
                    </Avatar>
                    <div>
                      <div
                        style={{
                          fontSize: '12px',
                          fontWeight: 500,
                          color: '#141414',
                        }}
                      >
                        {displayAgentName && displayAgentName.length > 19
                          ? `${displayAgentName.slice(0, 19)}...`
                          : displayAgentName || name}
                      </div>
                    </div>
                  </div>
                </Menu.Item>
              </Radio>
              {otherAgents.map(agent => (
                <Radio
                  value={agent.uid}
                  style={{
                    height: 'fit-content',
                    display: 'flex',
                    flexDirection: 'row-reverse',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '8px',
                    width: '100%',
                    paddingRight: '16px',
                    paddingLeft: '10px',
                  }}
                  className={style.appNavRadioGroup}
                  key={agent.uid}
                >
                  <Menu.Item
                    className={classNames(
                      style.appNavMenuCustomItem,
                      style.appNavMenuCustomItemChild,
                      style.appNavMenuCustomItemFooter,
                    )}
                    style={{ 
                      height: '50px', 
                      paddingInline: '0px',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                    onItemHover={handleAgentListItemHover}
                    onClick={() => handleAgentListItemClick(agent.uid)}
                    itemKey={agent.uid}
                    key={agent.uid}
                  >
                    <div
                      style={{ 
                        marginBottom: '0px', 
                        cursor: 'pointer',
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                      role="button"
                      tabIndex={0}
                      onClick={e => {
                        e.stopPropagation() // Prevent event bubbling to parent Radio
                        // Handle the navigation manually
                        const selectedAgent = aiAgentsBuilderList?.find(a => a.uid === agent.uid)
                        if (selectedAgent) {
                          localStorage.setItem('ccActiveAgentId', selectedAgent.uid)
                          dispatch({
                            type: 'apps/SET_STATE',
                            payload: {
                              activeBuilderAgent: selectedAgent,
                            },
                          })
                          // Close the popup
                          setOpenKeys([])
                          // Navigate to the same page but for the selected agent
                          try {
                            const currentPath = location.pathname
                            const newPath = currentPath.replace(
                              /(\/app\/[^/]+\/ai-agents\/)([^/]+)(\/.*)?/,
                              (match, prefix, _uid, suffix = '') => `${prefix}${selectedAgent.uid}${suffix}`,
                            )
                            if (newPath !== currentPath) {
                              history.push(newPath)
                            }
                          } catch (err) {
                            history.push(`/app/${apps.active?.id}/ai-agents/${selectedAgent.uid}/instructions`)
                          }
                        }
                      }}
                      onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          e.stopPropagation()
                          const selectedAgent = aiAgentsBuilderList?.find(a => a.uid === agent.uid)
                          if (selectedAgent) {
                            localStorage.setItem('ccActiveAgentId', selectedAgent.uid)
                            dispatch({
                              type: 'apps/SET_STATE',
                              payload: {
                                activeBuilderAgent: selectedAgent,
                            },
                          })
                            setOpenKeys([])
                            // Navigate to the same page but for the selected agent
                            try {
                              const currentPath = location.pathname
                              const newPath = currentPath.replace(
                                /(\/app\/[^/]+\/ai-agents\/)([^/]+)(\/.*)?/,
                                (match, prefix, _uid, suffix = '') => `${prefix}${selectedAgent.uid}${suffix}`,
                              )
                              if (newPath !== currentPath) {
                                history.push(newPath)
                              }
                            } catch (err) {
                              history.push(`/app/${apps.active?.id}/ai-agents/${selectedAgent.uid}/instructions`)
                            }
                          }
                        }
                      }}
                    >
                      <div
                        className={classNames(
                          style.appNavMenuResourcesWrapper,
                          style.appNavMenuAgentsWrapperName,
                        )}
                      >
                        <Avatar
                          src={agent.icon || null}
                          style={{
                            backgroundColor: '#F5F5F5',
                            color: '#727272',
                            fontSize: '14px',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            width: '32px',
                            height: '32px',
                            borderRadius: '8px',
                            flexShrink: 0,
                          }}
                        >
                          {agent.name ? agent.name.match(/[A-Za-z]/)?.[0].toUpperCase() : ''}
                        </Avatar>
                        <div>
                          <div
                            style={{
                              fontSize: '12px',
                              fontWeight: 500,
                              color: '#141414',
                            }}
                          >
                            {agent.name && agent.name.length > 19
                              ? `${agent.name.slice(0, 19)}...`
                              : agent.name}
                          </div>
                        </div>
                      </div>
                    </div>
                  </Menu.Item>
                </Radio>
              ))}
            </Space>
          </Radio.Group>

          <Menu.Item
            className={classNames(
              style.appNavMenuCustomItem,
              style.appNavMenuCustomItemChild,
              style.appNavMenuCustomItemFooter,
            )}
            style={{
              backgroundColor: '#FFF',
              width: '100%',
              margin: '0px',
              padding: '20px',
            }}
          >
            <NavLink to={`/app/${apps.active.id}/ai-agents`} style={{ marginBottom: '0px' }} exact>
              <div
                className={style.appNavMenuResourcesWrapper}
                style={{ color: '#6852D6', fontSize: '14px' }}
              >
                <div style={{ color: '#6852D6' }}>See All Agents</div>
              </div>
            </NavLink>
          </Menu.Item>

          {/* Note: Excluding "Create an agent" button as per requirements */}
        </Menu.SubMenu>
      </Menu>
    </Sider>
  )
}

export default AppNavCategoryHeader
